import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Box, Title, LoadingOverlay, TabsValue } from '@mantine/core';
import { IconUpload, IconPencil, IconCamera } from '@tabler/icons-react';
import { ProcessedBetSlipData } from '../../../backend/src/models/BetSlip';
import { FileWithPath } from '@mantine/dropzone';
import { processImage } from '../services/api';
import { notifications } from '@mantine/notifications';
import { BetSlipFileUpload } from './BetSlipFileUpload';
import { BetSlipManualEntry } from './BetSlipManualEntry';
import { BetSlipCameraCapture } from './BetSlipCameraCapture';

type UploadMethod = 'upload' | 'manual' | 'camera';

export function BetSlipUploadMethods() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<UploadMethod>('upload');
  const [loading, setLoading] = useState(false);

  const handleUploadStart = () => {
    setLoading(true);
  };

  const handleUploadEnd = () => {
    setLoading(false);
  };

  const handleFileUploadSuccess = (processedData: ProcessedBetSlipData) => {
    navigate('/review', {
      state: {
        processedData,
        imageUrl: processedData.imageUrl
      }
    });
  };

  const handleManualSubmit = (data: ProcessedBetSlipData) => {
    navigate('/review', {
      state: {
        processedData: {
          ...data,
          imageUrl: '', // No image for manual entry
          extractedText: '', // No OCR text for manual entry
        }
      }
    });
  };

  const handleCameraCapture = async (imageBlob: Blob) => {
    try {
      handleUploadStart();
      // Convert Blob to File
      const file = new File([imageBlob], 'camera-capture.jpg', { type: 'image/jpeg' });
      const processedData = await processImage(file as FileWithPath);
      console.log('Camera capture processed data:', {
        ...processedData,
        hasSelections: !!processedData.selections,
        selectionsLength: processedData.selections?.length,
        method: 'camera'
      });
      handleFileUploadSuccess(processedData);
    } catch (error) {
      console.error('Camera capture error:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to process camera capture',
        color: 'red'
      });
    } finally {
      handleUploadEnd();
    }
  };

  const handleTabChange = (value: TabsValue) => {
    if (value && (value === 'upload' || value === 'manual' || value === 'camera')) {
      setActiveTab(value as UploadMethod);
    }
  };

  return (
    <Box p="md" pos="relative">
      <LoadingOverlay visible={loading} />
      
      <Title order={2} mb="md">Add Bet Slip</Title>
      
      <Tabs defaultValue="upload" value={activeTab} onTabChange={handleTabChange}>
        <Tabs.List grow>
          <Tabs.Tab 
            value="upload" 
            icon={<IconUpload size="1.2rem" />}
          >
            Upload Image
          </Tabs.Tab>
          <Tabs.Tab 
            value="manual" 
            icon={<IconPencil size="1.2rem" />}
          >
            Manual Entry
          </Tabs.Tab>
          <Tabs.Tab 
            value="camera" 
            icon={<IconCamera size="1.2rem" />}
          >
            Camera
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="upload" pt="md">
          <BetSlipFileUpload
            onUploadStart={handleUploadStart}
            onUploadEnd={handleUploadEnd}
            onUploadSuccess={handleFileUploadSuccess}
          />
        </Tabs.Panel>

        <Tabs.Panel value="manual" pt="md">
          <BetSlipManualEntry
            onSubmit={handleManualSubmit}
          />
        </Tabs.Panel>

        <Tabs.Panel value="camera" pt="md">
          <BetSlipCameraCapture
            onCapture={handleCameraCapture}
          />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}